import React from "react";
import { useQuery } from "react-query";
import { useParams, Link } from "react-router-dom";
import { apiFetch, formatDateTime, titleCase } from "utils";
import { useAuthState, useAuthDispatch } from "context";
import LoadingIndicator from "components/LoadingIndicator";
import Error from "components/Error";
import ShelterBuddyResyncStatus from "../Resync/components/ShelterBuddyResyncStatus";

const ShelterBuddyResyncJobDetails = () => {
  const { token } = useAuthState();
  const dispatch = useAuthDispatch();
  const { jobId } = useParams();

  const {
    data: response,
    error,
    isLoading,
  } = useQuery(
    ["shelterbuddyResyncJob", jobId],
    () =>
      apiFetch({
        token,
        dispatch,
        endpoint: `/api/internal/shelterbuddy/resync_jobs/${jobId}`,
      }),
    {
      refetchInterval: (data) => {
        const status = data?.data?.attributes?.status;
        return status === "completed" || status === "failed" ? false : 3000;
      },
    }
  );

  if (isLoading) {
    return (
      <LoadingIndicator inline content={<>Loading job details&hellip;</>} />
    );
  }

  if (error) {
    return <Error>Error loading job details</Error>;
  }

  const job = response.data.attributes;

  const getStatusColor = (status) => {
    const colors = {
      pending: "text-yellow-600 bg-yellow-50 border-yellow-200",
      processing: "text-blue-600 bg-blue-50 border-blue-200",
      completed: "text-green-600 bg-green-50 border-green-200",
      failed: "text-red-600 bg-red-50 border-red-200",
    };
    return colors[status] || "text-gray-600 bg-gray-50 border-gray-200";
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link
            to="/shelterbuddy-api-management/resync-jobs"
            className="text-blue-600 hover:text-blue-800 mb-2 inline-block"
          >
            ← Back to Jobs List
          </Link>
          <h1 className="text-2xl font-bold">Resync Job #{response.data.id}</h1>
        </div>
        <span
          className={`px-4 py-2 rounded font-semibold border ${getStatusColor(
            job.status
          )}`}
        >
          {titleCase(job.status)}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg border p-6">
          <h3 className="font-semibold text-lg mb-4">Job Information</h3>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm text-gray-600">Job ID</dt>
              <dd className="font-medium">#{response.data.id}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-600">Date From</dt>
              <dd className="font-medium">
                {new Date(job.dateFrom).toLocaleDateString()}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-gray-600">Progress</dt>
              <dd className="font-medium">{job.progress}%</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-600">Created At</dt>
              <dd className="font-medium">{formatDateTime(job.createdAt)}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-600">Updated At</dt>
              <dd className="font-medium">{formatDateTime(job.updatedAt)}</dd>
            </div>
          </dl>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <h3 className="font-semibold text-lg mb-4">Progress Details</h3>
          <ShelterBuddyResyncStatus
            steps={{
              animalData:
                job.steps?.animalData || job.steps?.animal_data || "pending",
              pairing: job.steps?.pairing || "pending",
            }}
            jobStatus={job.status}
          />
        </div>
      </div>

      {job.errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="font-semibold text-lg text-red-800 mb-2">
            Error Details
          </h3>
          <p className="text-red-700 whitespace-pre-wrap">{job.errorMessage}</p>
        </div>
      )}
    </div>
  );
};

export default ShelterBuddyResyncJobDetails;
